import {
  audienceDefinitionSchema,
  nativeAudienceMutationInputSchema,
  type AudienceDefinition,
  type NativeAudienceCatalog,
  type NativeAudienceMutationInput,
  type NativeAudienceRecord,
} from '@charitystack/schemas';

export type NativeAudiencePersistenceIdentity = {
  actorId: string;
  sessionId: string;
  assistantSessionId: string | null;
  tabId: string;
};

export type NativeAudiencePersistenceOwner = {
  actorId: string;
  sessionId: string;
  tabId: string;
};

export type NativeAudienceDraftSnapshot = {
  name: string;
  description: string;
  definition: AudienceDefinition;
  targetToken: string;
};

export type NativeAudienceDraftBase = {
  segmentId: string | null;
  revision: string | null;
  targetToken: string;
};

export type StoredNativeAudienceDraft = {
  version: 3;
  writerId: string;
  savedAt: string;
  owner: NativeAudiencePersistenceOwner;
  base: NativeAudienceDraftBase;
  /** `templateKey`: the template a new segment started from, so "Continue draft" keeps its chip (ADM-199). */
  draft: NativeAudienceDraftSnapshot & { templateKey?: string };
  /**
   * The assistant card a Review in editor draft came from, so a save after a
   * reload still records its outcome in that chat (the handoff copy is gone).
   */
  proposalOutcome?: StoredProposalOutcome;
};

export type StoredProposalOutcome = { threadId: string; proposalId: string; proposalHash: string };

const MAX_OUTCOME_FIELD_LENGTH = 256;
function decodeProposalOutcome(value: unknown): StoredProposalOutcome | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { threadId, proposalId, proposalHash } = value as Record<string, unknown>;
  const valid = (field: unknown): field is string => typeof field === 'string' && field.length > 0 && field.length <= MAX_OUTCOME_FIELD_LENGTH;
  return valid(threadId) && valid(proposalId) && valid(proposalHash) ? { threadId, proposalId, proposalHash } : undefined;
}

/** A save failure the page explains with its own next step, kept so a reload says it again (ADM-199). */
export type StoredSaveFailureKind = 'conflict' | 'target-changed';

export type StoredNativeAudiencePending = {
  version: 2;
  writerId: string;
  owner: NativeAudiencePersistenceOwner;
  base: NativeAudienceDraftBase;
  input: NativeAudienceMutationInput;
  lastFailure?: StoredSaveFailureKind;
};

// Both optional fields came after their record versions: a decoder that predates them spreads the
// record and ignores them, and one that has them drops a malformed value rather than the record.
const MAX_TEMPLATE_KEY_LENGTH = 64;
const STORED_FAILURE_KINDS: readonly unknown[] = ['conflict', 'target-changed'] satisfies StoredSaveFailureKind[];

type StorageReaderWriter = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function keyPart(value: string) {
  return encodeURIComponent(value);
}

function ownerKey(identity: NativeAudiencePersistenceIdentity) {
  return `${keyPart(identity.actorId)}:${keyPart(identity.sessionId)}:${keyPart(identity.tabId)}`;
}

/** The org a segment's stored drafts are keyed by. */
export function segmentOrganizationId(merchant: unknown) {
  const record = merchant as {
    clerkOrganizationId?: unknown;
    organizationId?: unknown;
  } | undefined;
  return typeof record?.clerkOrganizationId === 'string'
    ? record.clerkOrganizationId
    : typeof record?.organizationId === 'string'
      ? record.organizationId
      : 'unknown-organization';
}

export function nativeAudienceDraftStorageKey(
  organizationId: string,
  segmentId: string | undefined,
  identity: NativeAudiencePersistenceIdentity,
) {
  return `native-audience-draft:v3:${keyPart(organizationId)}:${keyPart(segmentId ?? 'new')}:${ownerKey(identity)}`;
}

export function nativeAudiencePendingStorageKey(
  organizationId: string,
  segmentId: string | undefined,
  identity: NativeAudiencePersistenceIdentity,
) {
  return `native-audience-pending:v2:${keyPart(organizationId)}:${keyPart(segmentId ?? 'new')}:${ownerKey(identity)}`;
}

export function replaceStorageValue(
  storage: StorageReaderWriter,
  key: string,
  expectedValue: string | null,
  nextValue: string,
) {
  try {
    if (storage.getItem(key) !== expectedValue) return false;
    storage.setItem(key, nextValue);
    return storage.getItem(key) === nextValue;
  } catch {
    return false;
  }
}

export function removeStorageValue(
  storage: StorageReaderWriter,
  key: string,
  expectedValue: string,
) {
  try {
    if (storage.getItem(key) !== expectedValue) return false;
    storage.removeItem(key);
    return storage.getItem(key) === null;
  } catch {
    return false;
  }
}

export function nativeAudienceDraftBase(
  catalog: NativeAudienceCatalog,
  record?: NativeAudienceRecord,
): NativeAudienceDraftBase {
  return {
    segmentId: record?.id ?? null,
    revision: record?.revision ?? null,
    targetToken: catalog.targetToken,
  };
}

export function nativeAudiencePersistenceOwner(
  identity: NativeAudiencePersistenceIdentity,
): NativeAudiencePersistenceOwner {
  return { actorId: identity.actorId, sessionId: identity.sessionId, tabId: identity.tabId };
}

export function decodeStoredNativeAudienceDraft(value: string | null): StoredNativeAudienceDraft | null {
  if (!value) return null;
  let parsed: Partial<StoredNativeAudienceDraft>;
  try {
    parsed = JSON.parse(value) as Partial<StoredNativeAudienceDraft>;
  } catch {
    return null;
  }
  if (
    parsed.version !== 3 ||
    typeof parsed.writerId !== 'string' ||
    typeof parsed.savedAt !== 'string' ||
    !validOwner(parsed.owner) ||
    !validBase(parsed.base) ||
    !parsed.draft ||
    typeof parsed.draft.name !== 'string' ||
    typeof parsed.draft.description !== 'string' ||
    typeof parsed.draft.targetToken !== 'string'
  ) {
    return null;
  }
  const definition = decodeEditableDefinition(parsed.draft.definition);
  if (!definition) return null;
  const { templateKey, ...snapshot } = parsed.draft;
  const validTemplateKey = typeof templateKey === 'string' && templateKey.length > 0 && templateKey.length <= MAX_TEMPLATE_KEY_LENGTH;
  const { proposalOutcome: rawOutcome, ...record } = parsed as StoredNativeAudienceDraft;
  const proposalOutcome = decodeProposalOutcome(rawOutcome);
  return {
    ...record,
    draft: { ...snapshot, definition, ...(validTemplateKey ? { templateKey } : {}) },
    ...(proposalOutcome ? { proposalOutcome } : {}),
  };
}

export function restoreStoredNativeAudienceDraft(
  value: string | null,
  identity: NativeAudiencePersistenceIdentity,
  base: NativeAudienceDraftBase,
) {
  const stored = decodeStoredNativeAudienceDraft(value);
  return stored && ownerMatches(stored.owner, identity) && baseMatches(stored.base, base)
    ? stored
    : null;
}

export function decodeStoredNativeAudiencePending(value: string | null): StoredNativeAudiencePending | null {
  if (!value) return null;
  let parsed: Partial<StoredNativeAudiencePending>;
  try {
    parsed = JSON.parse(value) as Partial<StoredNativeAudiencePending>;
  } catch {
    return null;
  }
  if (
    parsed.version !== 2 ||
    typeof parsed.writerId !== 'string' ||
    !validOwner(parsed.owner) ||
    !validBase(parsed.base)
  ) return null;
  const input = nativeAudienceMutationInputSchema.safeParse(parsed.input);
  if (!input.success) return null;
  const command = input.data;
  // The envelope cannot rebase or retarget a command it claims to recover.
  if (command.targetToken !== parsed.base!.targetToken ||
    ('segmentId' in command ? command.segmentId : null) !== parsed.base!.segmentId ||
    ('expectedRevision' in command ? command.expectedRevision : null) !== parsed.base!.revision) return null;
  const { lastFailure, ...record } = parsed as StoredNativeAudiencePending;
  return { ...record, input: command, ...(STORED_FAILURE_KINDS.includes(lastFailure) ? { lastFailure } : {}) };
}

export function restoreStoredNativeAudiencePending(
  value: string | null,
  identity: NativeAudiencePersistenceIdentity,
  base: NativeAudienceDraftBase,
  options: { allowRevisionRecovery?: boolean } = {},
) {
  const stored = decodeStoredNativeAudiencePending(value);
  const sameTarget = stored && stored.base.segmentId === base.segmentId && stored.base.targetToken === base.targetToken;
  return stored && ownerMatches(stored.owner, identity) &&
    (baseMatches(stored.base, base) || (options.allowRevisionRecovery && sameTarget))
    ? stored
    : null;
}

/**
 * A request this tab kept after its marketing account changed (ADM-199). The reload that change
 * asks for loads the new account, so the request no longer restores; it is recognised here so the
 * page can say the account changed and offer its one next step, instead of a generic mismatch.
 */
export function isStoredAccountChangedRequest(
  value: string | null,
  identity: NativeAudiencePersistenceIdentity,
  base: NativeAudienceDraftBase,
) {
  const stored = decodeStoredNativeAudiencePending(value);
  return Boolean(stored && ownerMatches(stored.owner, identity) &&
    stored.base.segmentId === base.segmentId &&
    stored.base.targetToken !== base.targetToken &&
    stored.lastFailure === 'target-changed');
}

/** The template this tab's stored draft started from, whatever its base: the key only, never its rules. */
export function storedDraftTemplateKey(value: string | null, identity: NativeAudiencePersistenceIdentity) {
  const stored = decodeStoredNativeAudienceDraft(value);
  return stored && ownerMatches(stored.owner, identity) ? stored.draft.templateKey : undefined;
}

export function removeStoredNativeAudienceDraft({
  storage,
  key,
  expectedValue,
  identity,
  writerId,
}: {
  storage: StorageReaderWriter;
  key: string;
  expectedValue: string | null;
  identity: NativeAudiencePersistenceIdentity;
  writerId: string;
}) {
  if (!expectedValue) return false;
  const stored = decodeStoredNativeAudienceDraft(expectedValue);
  return Boolean(
    stored &&
    ownerMatches(stored.owner, identity) &&
    stored.writerId === writerId &&
    removeStorageValue(storage, key, expectedValue),
  );
}

/**
 * Cancel discards this tab's stored draft, whichever page in the tab wrote it
 * (a restored draft keeps its earlier writer until the next edit). Only bytes
 * this tab owns and that are unchanged since they were read are removed.
 */
export function discardStoredNativeAudienceDraft({
  storage,
  key,
  expectedValue,
  identity,
}: {
  storage: StorageReaderWriter;
  key: string;
  expectedValue: string | null;
  identity: NativeAudiencePersistenceIdentity;
}) {
  if (!expectedValue) return false;
  const stored = decodeStoredNativeAudienceDraft(expectedValue);
  return Boolean(
    stored &&
    ownerMatches(stored.owner, identity) &&
    removeStorageValue(storage, key, expectedValue),
  );
}

export function removeStoredNativeAudiencePending({
  storage,
  key,
  expectedValue,
  identity,
  writerId,
  requestId,
}: {
  storage: StorageReaderWriter;
  key: string;
  expectedValue: string | null;
  identity: NativeAudiencePersistenceIdentity;
  writerId: string;
  requestId: string;
}) {
  if (!expectedValue) return false;
  const stored = decodeStoredNativeAudiencePending(expectedValue);
  return Boolean(
    stored &&
    ownerMatches(stored.owner, identity) &&
    stored.writerId === writerId &&
    stored.input.requestId === requestId &&
    removeStorageValue(storage, key, expectedValue),
  );
}

/**
 * This tab's stored work on a new segment (QA F-33, D4): a draft, and a save request that never
 * settled. Picking a template or a blank builder on the entry screen starts fresh, so the entry
 * screen asks before it discards a draft, and never discards an unsettled request: the server
 * may have run it, and the request is the only way to find out.
 */
export function storedNewSegmentWork(storage: StorageReaderWriter, organizationId: string, identity: NativeAudiencePersistenceIdentity): { draft: boolean; pending: boolean } {
  try {
    const draft = decodeStoredNativeAudienceDraft(storage.getItem(nativeAudienceDraftStorageKey(organizationId, undefined, identity)));
    const pending = decodeStoredNativeAudiencePending(storage.getItem(nativeAudiencePendingStorageKey(organizationId, undefined, identity)));
    return { draft: Boolean(draft && ownerMatches(draft.owner, identity)), pending: Boolean(pending && ownerMatches(pending.owner, identity)) };
  } catch {
    return { draft: false, pending: false };
  }
}

/** Removes this tab's stored new-segment draft; bytes another owner wrote, and any save request, are left alone. */
export function discardStoredNewSegmentDraft(storage: StorageReaderWriter, organizationId: string, identity: NativeAudiencePersistenceIdentity): boolean {
  try {
    const key = nativeAudienceDraftStorageKey(organizationId, undefined, identity);
    const raw = storage.getItem(key);
    const draft = decodeStoredNativeAudienceDraft(raw);
    return !raw || !draft || !ownerMatches(draft.owner, identity) || removeStorageValue(storage, key, raw);
  } catch {
    return false;
  }
}

function ownerMatches(
  owner: NativeAudiencePersistenceOwner,
  identity: NativeAudiencePersistenceIdentity,
) {
  return owner.actorId === identity.actorId &&
    owner.sessionId === identity.sessionId &&
    owner.tabId === identity.tabId;
}

function baseMatches(left: NativeAudienceDraftBase, right: NativeAudienceDraftBase) {
  return left.segmentId === right.segmentId &&
    left.revision === right.revision &&
    left.targetToken === right.targetToken;
}

function validOwner(value: unknown): value is NativeAudiencePersistenceOwner {
  const owner = value as Partial<NativeAudiencePersistenceOwner> | undefined;
  return typeof owner?.actorId === 'string' &&
    typeof owner.sessionId === 'string' &&
    typeof owner.tabId === 'string';
}

function validBase(value: unknown): value is NativeAudienceDraftBase {
  const base = value as Partial<NativeAudienceDraftBase> | undefined;
  return (base?.segmentId === null || typeof base?.segmentId === 'string') &&
    (base?.revision === null || typeof base?.revision === 'string') &&
    typeof base?.targetToken === 'string';
}

// Drafts may contain an unfinished value, but never arbitrary rules or unbounded
// trees. Validate the exact structure with placeholders only for editable values;
// return the original draft. Saving and preview still use the strict wire schema.
// Unfinished values include a cleared number (NaN is stored as JSON null) and a
// rule group that has no rules yet.
const unfinishedNumber = (value: unknown) => value === null || (typeof value === 'number' && Number.isFinite(value));
const PLACEHOLDER_CONDITION = { type: 'presence', field: 'city', operator: 'is-set' };
function decodeEditableDefinition(value: unknown): AudienceDefinition | null {
  const complete = audienceDefinitionSchema.safeParse(value);
  if (complete.success) return complete.data;
  let copy: unknown;
  try { copy = JSON.parse(JSON.stringify(value)); } catch { return null; }
  if (!copy || typeof copy !== 'object') return null;
  const definition = copy as Record<string, unknown>;
  const pending = [{ value: definition.rule, depth: 0 }];
  let count = 0;
  while (pending.length && definition.kind !== 'fixed' && definition.kind !== 'legacy') {
    const entry = pending.pop()!;
    if (++count > 100 || entry.depth > 6 || !entry.value || typeof entry.value !== 'object') return null;
    const node = entry.value as Record<string, unknown>;
    if (node.type === 'all' || node.type === 'any') {
      if (!Array.isArray(node.children)) return null;
      if (node.children.length === 0) node.children = [{ ...PLACEHOLDER_CONDITION }];
      else pending.push(...node.children.map((child) => ({ value: child, depth: entry.depth + 1 })));
    } else if (node.type === 'not') pending.push({ value: node.child, depth: entry.depth + 1 });
    else if (node.type === 'text' && Array.isArray(node.values)) {
      if (node.values.some((item) => typeof item !== 'string' || item.length > 256)) return null;
      node.values = node.values.length ? node.values.map((item) => item || 'draft') : ['draft'];
    } else if (node.type === 'list' && node.value === '') node.value = 'draft';
    else if (node.type === 'date' && node.operator !== 'age') {
      for (const field of node.operator === 'between' ? ['start', 'end'] : ['value']) {
        if (typeof node[field] !== 'string' || (node[field] as string).length > 256) return null;
        node[field] = field === 'end' ? '2000-01-02T00:00:00Z' : '2000-01-01T00:00:00Z';
      }
    } else if (node.type === 'date' && node.operator === 'age' && unfinishedNumber(node.amount)) node.amount = 0;
    else if (node.type === 'giving' && unfinishedNumber(node.value)) node.value = 0;
    else if (node.type === 'number' && unfinishedNumber(node.value)) node.value = 0;
  }
  const shape = audienceDefinitionSchema.safeParse(copy);
  if (!shape.success || (shape.data.kind !== 'dynamic' && shape.data.kind !== 'hybrid')) return null;
  const rule = JSON.parse(JSON.stringify((value as { rule: unknown }).rule)) as typeof shape.data.rule;
  restoreClearedNumbers(rule);
  return { ...shape.data, rule };
}

// JSON turns the editor's cleared number (NaN) into null; give it back as NaN so
// the restored draft reads exactly like the unfinished one the person left.
function restoreClearedNumbers(rule: unknown) {
  const pending = [rule];
  while (pending.length) {
    const node = pending.pop() as Record<string, unknown> | null;
    if (!node || typeof node !== 'object') continue;
    if (Array.isArray(node.children)) pending.push(...node.children);
    if (node.type === 'not') pending.push(node.child);
    if ((node.type === 'number' || node.type === 'giving') && node.value === null) node.value = Number.NaN;
    if (node.type === 'date' && node.amount === null) node.amount = Number.NaN;
  }
}
